import React from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CircleDollarSign, Layers, CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency } from "@/utils/currency";

interface PaymentsHistoryProps {
  influencerId: string;
}

interface CampaignInfo {
  id: string;
  name: string;
  start_date: string | null;
}

interface CampaignInfluencerInfo {
  influencer_id: string;
  campaigns: CampaignInfo | null;
}

interface PaymentRecord {
  id: string;
  created_at: string | null;
  invoice_number: string | null;
  net_payable: number | null;
  status: string;
  payment_mode: string | null;
  due_date: string | null;
  campaign_influencers: CampaignInfluencerInfo | null;
}

export default function PaymentsHistory({ influencerId }: PaymentsHistoryProps) {
  const { userCountry } = useAuth();
  const { data: payments = [], isLoading } = useQuery<PaymentRecord[]>({
    queryKey: ['influencer-payments', influencerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select(`
          *,
          campaign_influencers!inner (
            influencer_id,
            campaigns (
              id,
              name,
              start_date
            )
          )
        `)
        .eq('campaign_influencers.influencer_id', influencerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data as unknown) as PaymentRecord[];
    },
  });

  const isOverdue = (dueDate: string | null, status: string) => {
    if (!dueDate || status === 'paid') return false;
    return new Date(dueDate) < new Date();
  };

  const filterPayments = (filter: string) => {
    switch (filter) {
      case 'paid':
        return payments.filter(p => p.status === 'paid');
      case 'pending':
        return payments.filter(p => p.status === 'pending');
      case 'overdue':
        return payments.filter(p => isOverdue(p.due_date, p.status));
      default:
        return payments;
    }
  };

  const getTotalAmount = (filter: string) => {
    if (filter === 'all') {
      // Amount in the right of All and paid is the total amount which is paid
      return payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (Number(p.net_payable) || 0), 0);
    }
    return filterPayments(filter).reduce((sum, p) => sum + (Number(p.net_payable) || 0), 0);
  };

  const formatDateShort = (dateStr?: string | null) => {
    if (!dateStr) return "TBD";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  if (isLoading) {
    return (
      <div className="w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <CircleDollarSign className="w-5 h-5" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">Payments History</h2>
        </div>
        <div className="text-center text-gray-500 py-8">Loading payment history...</div>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
          <CircleDollarSign className="w-5 h-5" />
        </div>
        <h2 className="text-xl font-bold text-slate-900 tracking-tight">Payments History</h2>
      </div>

      <Tabs defaultValue="all" className="w-full space-y-6">
        <TabsList className="bg-white p-0 rounded-xl flex items-stretch w-full sm:w-fit border border-slate-200 h-12 overflow-x-auto scrollbar-none justify-start whitespace-nowrap">
          <TabsTrigger
            value="all"
            className="flex-1 sm:flex-initial h-full px-5 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-800 transition-all border-b-2 border-transparent data-[state=active]:border-[#A655F8] data-[state=active]:text-[#A655F8] data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <Layers className="w-4 h-4 shrink-0" />
            <span>All ({payments.length})</span>
            <span className="ml-1.5 font-semibold">{formatCurrency(getTotalAmount('all'), userCountry, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </TabsTrigger>
          <div className="h-5 w-px bg-slate-200 self-center shrink-0" />
          <TabsTrigger
            value="paid"
            className="flex-1 sm:flex-initial h-full px-5 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-800 transition-all border-b-2 border-transparent data-[state=active]:border-[#A655F8] data-[state=active]:text-[#A655F8] data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>Paid ({filterPayments('paid').length})</span>
            <span className="ml-1.5 font-semibold">{formatCurrency(getTotalAmount('paid'), userCountry, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </TabsTrigger>
          <div className="h-5 w-px bg-slate-200 self-center shrink-0" />
          <TabsTrigger
            value="pending"
            className="flex-1 sm:flex-initial h-full px-5 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-800 transition-all border-b-2 border-transparent data-[state=active]:border-[#A655F8] data-[state=active]:text-[#A655F8] data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <Clock className="w-4 h-4 shrink-0" />
            <span>Pending ({filterPayments('pending').length})</span>
            <span className="ml-1.5 font-semibold">{formatCurrency(getTotalAmount('pending'), userCountry, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </TabsTrigger>
          <div className="h-5 w-px bg-slate-200 self-center shrink-0" />
          <TabsTrigger
            value="overdue"
            className="flex-1 sm:flex-initial h-full px-5 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium text-slate-800 transition-all border-b-2 border-transparent data-[state=active]:border-[#A655F8] data-[state=active]:text-[#A655F8] data-[state=active]:font-semibold data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
          >
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>Overdue ({filterPayments('overdue').length})</span>
            <span className="ml-1.5 font-semibold">{formatCurrency(getTotalAmount('overdue'), userCountry, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
          </TabsTrigger>
        </TabsList>

        {['all', 'paid', 'pending', 'overdue'].map((filter) => (
          <TabsContent key={filter} value={filter} className="mt-0">
            {filterPayments(filter).length === 0 ? (
              <div className="text-center text-gray-800 py-8 border border-slate-100 rounded-2xl bg-white">
                No {filter === 'all' ? '' : filter} payments found.
              </div>
            ) : (
              <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white overflow-x-auto">
                <Table>
                  <TableHeader className="bg-[#F8F9FF]">
                    <TableRow className="bg-[#F8F9FF] hover:bg-[#F8F9FF] border-b border-slate-200">
                      <TableHead className="font-bold text-[14px] text-black tracking-wider h-14 uppercase px-6">INVOICE ID</TableHead>
                      <TableHead className="font-bold text-[14px] text-black tracking-wider h-14 uppercase px-6">CAMPAIGN</TableHead>
                      <TableHead className="font-bold text-[14px] text-black tracking-wider h-14 uppercase px-6">DATE</TableHead>
                      <TableHead className="font-bold text-[14px] text-black tracking-wider h-14 uppercase px-6">AMOUNT</TableHead>
                      <TableHead className="font-bold text-[14px] text-black tracking-wider h-14 uppercase px-6">STATUS</TableHead>
                      <TableHead className="font-bold text-[14px] text-black tracking-wider h-14 uppercase px-6">PAYMENT<br />METHOD</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filterPayments(filter).map((payment) => (
                      <TableRow key={payment.id} className="hover:bg-slate-50/50 border-b border-slate-300 last:border-0">
                        <TableCell className="font-medium text-[14px] text-slate-900 py-4 px-6">
                          {payment.invoice_number || `INV-${new Date(payment.created_at || '').getFullYear()}-${String(payment.id).slice(0, 3).toUpperCase()}`}
                        </TableCell>
                        <TableCell className="font-medium text-[14px] text-slate-600 py-4 px-6">
                          {payment.campaign_influencers?.campaigns?.name || 'Unknown Campaign'}
                        </TableCell>
                        <TableCell className="text-slate-600 font-medium text-[14px] py-4 px-6">
                          {formatDateShort(payment.campaign_influencers?.campaigns?.start_date)}
                        </TableCell>
                        <TableCell className="font-medium text-[14px] text-slate-900 py-4 px-6">
                          {formatCurrency(payment.net_payable || 0, userCountry, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell className="py-4 px-6">
                          <span className={`inline-flex px-3 py-1 rounded-xl text-xs font-medium ${payment.status === 'paid'
                            ? 'bg-[#E6F9F0] text-[#00A854]'
                            : payment.status === 'pending'
                              ? 'bg-[#FEF3C7] text-[#D97706]'
                              : 'bg-[#FEE2E2] text-[#DC2626]'
                            }`}>
                            {payment.status === 'paid' ? 'Paid' : payment.status === 'pending' ? 'Pending' : 'Overdue'}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-600 font-medium text-[14px] py-4 px-6">
                          {payment.payment_mode ? String(payment.payment_mode).replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Bank Transfer'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
